/* eslint-disable array-bracket-newline */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IUser, Network } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import { formatAddress } from '../../../helpers/formatters';
import WalletBalanceMenuTab from './WalletBalanceMenu';
import PreviousWallets from './PreviousWallets';
import CEXWallet from './CEXWallet';
import { onCopyAddress, onWalletScan } from '../../../helpers/addressUtils';
import Badge from '../../../molecules/Badge';
import Image from '../../../atoms/Image';
import WalletStatistics from './WalletStatistics';
import SidebarProfile from './SidebarProfile';
import { IStore } from '../../../reducers';
import { UserAPI } from '../../../store/createStore';
import NumberDisplay from '../../../molecules/NumberDisplay';
import { WalletTokensBySym } from '../../../wallet/walletReducer';

import statsIcon from '../../../assets/images/clock_with_doc.svg';

import './styles.scss';

enum EWalletTabs {
  Main,
  Stats,
}

interface ISideBarWalletProps {
  error: boolean;
  mapping: WalletTokensBySym;
  network: Network;
  loading: boolean;
  address: string;
  balance: number;
  nativeToken: string;
  walletSidName: string | null;
  onLogout(): void;
  onClose(): void;
}

const SideBarWallet = ({
  address,
  balance,
  nativeToken,
  walletSidName,
  error,
  network,
  mapping,
  loading,
  onLogout,
  onClose,
}: ISideBarWalletProps) => {
  const refresh = useSelector((state: IStore) => state.competition.refresh);

  const [tab, setTab] = useState(EWalletTabs.Main);
  const [user, setUser] = useState<IUser | null>(null);
  const [userLoading, setUserLoading] = useState(true);

  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      (ref.current as HTMLElement).classList.add(
        'global_sidebar_mobile--isVisible'
      );
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    setUserLoading(true);

    const internalNetwork = network.getSymbol();
    let userNetwork = 'EVM';
    if (internalNetwork == 'ELROND') {
      userNetwork = 'ELROND';
    } else if (internalNetwork == 'TRON') {
      userNetwork = 'TRON';
    } else if (internalNetwork == 'TON') {
      userNetwork = 'TON';
    }

    UserAPI.fetchUser(address, userNetwork, controller).then((data) => {
      setUser(data);
      setUserLoading(false);
    });

    return () => {
      controller.abort();
    };
  }, [address, network, refresh]);

  const handleClose = () => {
    if (ref.current) {
      (ref.current as HTMLElement).classList.remove(
        'global_sidebar_mobile--isVisible'
      );
    }
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleLogout = () => {
    onLogout();
    handleClose();
  };

  return (
    <>
      <div className='connected_wallet__backdrop' onClick={handleClose} />
      <div className='select_wallet' ref={ref}>
        <div className='select_wallet__body'>
          <div className='select_wallet__body__active'>
            <div className='select_wallet__body__active__header'>
              {tab === EWalletTabs.Main ? (
                <div
                  className='select_wallet__close icon-close'
                  onClick={handleClose}
                />
              ) : (
                <div
                  className='select_wallet__close icon-corner-bottom-left'
                  onClick={() => setTab(EWalletTabs.Main)}
                />
              )}
            </div>
            <SidebarProfile
              user={user}
              userLoading={userLoading}
              chain={network.getSymbol()}
            />
            <Text size={12} className='select_wallet__body__active__title'>
              The wallet in default
            </Text>
            <div className='select_wallet__body__active__content'>
              <Image
                src={network.getImage()}
                size='l'
                className='select_wallet__body__active__content__chain'
              />
              <div className='select_wallet__body__active__content__description'>
                <div className='select_wallet__body__active__content__description__wallet'>
                  <div className='select_wallet__body__active__content__description__wallet__address'>
                    <Text size={12} className='text--navy-600'>
                      {walletSidName || formatAddress(address)}
                    </Text>
                  </div>
                  <div className='select_wallet__body__active__content__description__wallet__actions'>
                    <div
                      className='select_wallet__body__active__content__description__wallet__actions__icon icon-copy'
                      onClick={() => onCopyAddress(address)}
                    />
                    <div
                      className='select_wallet__body__active__content__description__wallet__actions__icon icon-top-right-square'
                      onClick={() => onWalletScan(address, network.getSymbol())}
                    />
                  </div>
                  <Badge variant='fill' klass='green' text='Active' />
                </div>
                <div className='wallet_amount'>
                  <NumberDisplay
                    value={balance}
                    textProps={{
                      size: 14,
                      className: 'text--weight-500 text--grey-200',
                    }}
                  />
                  <Text size={14} className='text--grey-400'>
                    {nativeToken}
                  </Text>
                </div>
              </div>
              <div
                className='select_wallet__body__active__content__logout'
                onClick={handleLogout}
              >
                <div className='select_wallet__body__active__content__logout__icon icon-logout-alt' />
              </div>
              <div className='select_wallet__body__active__content__statistics_btn'>
                {tab === EWalletTabs.Main ? (
                  <div
                    className='select_wallet__body__add'
                    onClick={() => setTab(EWalletTabs.Stats)}
                  >
                    <Image size='s' src={statsIcon} />
                    <Text size={12} className='text--weight-600 text--white'>
                      Details statistics
                    </Text>
                  </div>
                ) : (
                  <div
                    className='select_wallet__close icon-corner-bottom-left'
                    onClick={() => setTab(EWalletTabs.Main)}
                  />
                )}
              </div>
            </div>
          </div>

          {tab === EWalletTabs.Main ? (
            <>
              <PreviousWallets
                user={user}
                address={address}
                network={network}
                balance={balance}
                nativeToken={nativeToken}
              />

              <WalletBalanceMenuTab
                error={error}
                loading={loading}
                mapping={mapping}
                network={network}
                onClose={handleClose}
              />
              <CEXWallet />
            </>
          ) : (
            <WalletStatistics />
          )}
        </div>
      </div>
    </>
  );
};

export default SideBarWallet;
